import { fetchWithOrquestadorAuthorization } from '../auth/orquestadorApiClient';

const GOBIERNO_API_FALLBACK_URL = 'https://orquestador-steel-fastapi-a62df4c6727a.herokuapp.com';
const GOBIERNO_API_BASE_URL = (
  process.env.REACT_APP_GOBIERNO_API_BASE_URL
  || (process.env.NODE_ENV === 'production' ? GOBIERNO_API_FALLBACK_URL : '')
).replace(/\/$/, '');

const ORQUESTADOR_UPDATED_AT_STORAGE_KEY = 'orquestadorUpdatedAtLabelV1';

function readStoredUpdatedAtLabel() {
  if (typeof window === 'undefined') {
    return '';
  }

  try {
    return window.localStorage.getItem(ORQUESTADOR_UPDATED_AT_STORAGE_KEY) || '';
  } catch {
    return '';
  }
}

function storeUpdatedAtLabel(label) {
  if (typeof window === 'undefined' || !label) {
    return;
  }

  try {
    window.localStorage.setItem(ORQUESTADOR_UPDATED_AT_STORAGE_KEY, label);
  } catch {
    // El valor sigue disponible en memoria aunque el navegador bloquee el storage.
  }
}

let cachedUpdatedAtLabel = readStoredUpdatedAtLabel();
let updatedAtRequest = null;
let hasFetchedUpdatedAtForCurrentPageLoad = false;

function parseUpdatedAtValue(value) {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return { date: value, hasTime: true };
  }

  if (typeof value !== 'string' || !value.trim()) {
    return null;
  }

  const trimmedValue = value.trim();
  const dateTimeMatch = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/.exec(trimmedValue);

  if (dateTimeMatch) {
    const date = new Date(
      Number(dateTimeMatch[1]),
      Number(dateTimeMatch[2]) - 1,
      Number(dateTimeMatch[3]),
      Number(dateTimeMatch[4]),
      Number(dateTimeMatch[5]),
      Number(dateTimeMatch[6] || 0)
    );

    return Number.isNaN(date.getTime()) ? null : { date, hasTime: true };
  }

  const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmedValue);

  if (dateOnlyMatch) {
    const date = new Date(
      Number(dateOnlyMatch[1]),
      Number(dateOnlyMatch[2]) - 1,
      Number(dateOnlyMatch[3])
    );

    return Number.isNaN(date.getTime()) ? null : { date, hasTime: false };
  }

  const date = new Date(trimmedValue);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return {
    date,
    hasTime: /(?:T|\s)\d{2}:\d{2}/.test(trimmedValue),
  };
}

function formatUpdatedAtLabel(value) {
  const parsedValue = parseUpdatedAtValue(value);

  if (!parsedValue) {
    return '';
  }

  const { date, hasTime } = parsedValue;
  const day = `${date.getDate()}`.padStart(2, '0');
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const year = `${date.getFullYear()}`;
  const dateLabel = `${day}/${month}/${year}`;

  if (!hasTime) {
    return dateLabel;
  }

  const hours = `${date.getHours()}`.padStart(2, '0');
  const minutes = `${date.getMinutes()}`.padStart(2, '0');
  return `${dateLabel} · ${hours}:${minutes}`;
}

function getUpdatedAtValue(payload) {
  if (!payload || typeof payload !== 'object') {
    return payload || null;
  }

  return (
    payload.fecha
    || payload.fecha_actualizacion
    || payload.fecha_actualizaci\u00f3n
    || payload.fecha_hora
    || payload.updated_at
    || payload.timestamp
    || null
  );
}

export function getCachedOrquestadorUpdatedAtLabel() {
  return cachedUpdatedAtLabel;
}

export async function fetchOrquestadorUpdatedAtLabel(signal) {
  if (hasFetchedUpdatedAtForCurrentPageLoad) {
    return cachedUpdatedAtLabel;
  }

  if (updatedAtRequest) {
    return updatedAtRequest;
  }

  updatedAtRequest = (async () => {
    try {
      const response = await fetchWithOrquestadorAuthorization(
        `${GOBIERNO_API_BASE_URL}/global/fecha-actualizacion`,
        { signal }
      );

      if (!response.ok) {
        return cachedUpdatedAtLabel;
      }

      const nextLabel = formatUpdatedAtLabel(getUpdatedAtValue(await response.json()));

      if (nextLabel) {
        cachedUpdatedAtLabel = nextLabel;
        storeUpdatedAtLabel(nextLabel);
      }

      return cachedUpdatedAtLabel;
    } catch (error) {
      if (error.name === 'AbortError') {
        throw error;
      }

      return cachedUpdatedAtLabel;
    } finally {
      if (!signal?.aborted) {
        hasFetchedUpdatedAtForCurrentPageLoad = true;
      }
      updatedAtRequest = null;
    }
  })();

  return updatedAtRequest;
}
